/**
 * ExpensesTable — Ausgaben-Tabelle (ab lg) bzw. Karten (darunter, kein horizontales Scrollen).
 * Stil nach sevdesk-DNA: Satzschreibung-Köpfe, Pastell-Badges mit Punkt, Belegnummer
 * unterstrichen wenn Beleg vorhanden, bezahlte Fälligkeit durchgestrichen.
 */
import { ArrowDown, ArrowUp, Banknote, CircleDollarSign, Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { EXPENSE_STATUS } from './expenseStatus';
import { dueLabel, nettoOf, offenOf } from './expenseListModel';
import { TH } from './dna';

const neg = (n) => (n ? -n : 0); // Ausgaben negativ wie sevdesk, ohne "-0,00 €"
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const TONE = { neutral: 'text-dna-muted', warn: 'font-semibold text-dna-due-fg', error: 'font-semibold text-dna-due-fg', paid: 'text-dna-placeholder line-through' };

export function StatusBadge({ label, className }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-semibold', className)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />{label}
    </span>
  );
}

function categoryLabel(row, catName) {
  const ids = [...new Set((row.positionen || []).map((p) => p.category_id).filter(Boolean))];
  if (ids.length > 1) return 'Mehrere';
  return catName.get(Number(ids[0] ?? row.category_id)) || null;
}

function BelegNr({ row }) {
  const nr = row.beleg_nr || row.expense_no || '—';
  return <span className={cn('truncate font-mono text-[13px] text-dna-text', row.file_path && 'underline decoration-dna-field underline-offset-4')}>{nr}</span>;
}

function RowMenu({ row, onOpen, onViewBeleg, onMarkPaid, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Aktionen" onClick={(e) => e.stopPropagation()}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-dna-muted hover:bg-dna-warm hover:text-dna-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus">
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-lg border-dna-line p-1" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem className="h-10 gap-2 text-dna-primary" onClick={() => onOpen(row)}>
          <Pencil className="h-4 w-4" /> {row.view === 'zu_pruefen' ? 'Prüfen & buchen' : 'Öffnen'}
        </DropdownMenuItem>
        {row.file_path && <DropdownMenuItem className="h-10 gap-2 text-dna-primary" onClick={() => onViewBeleg(row)}><Eye className="h-4 w-4" /> Beleg ansehen</DropdownMenuItem>}
        {isOpen(row) && (
          <DropdownMenuItem className="h-10 gap-2 text-dna-primary" onClick={() => onMarkPaid(row)}><CircleDollarSign className="h-4 w-4" /> Als bezahlt markieren</DropdownMenuItem>
        )}
        <DropdownMenuItem className="h-10 gap-2 text-dna-due-fg" onClick={() => onDelete(row)}><Trash2 className="h-4 w-4" /> Löschen</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const isOpen = (row) => ['offen', 'faellig', 'teilbezahlt'].includes(row.view);

/** Geldschein: Zahlung verknüpfen (nur offene Rechnungen) */
function PayButton({ row, onMarkPaid }) {
  if (!isOpen(row)) return <span className="hidden w-11 lg:inline-block" aria-hidden="true" />;
  return (
    <button type="button" title="Als bezahlt markieren" aria-label={`Zahlung verknüpfen: ${row.lieferant_name || 'Ausgabe'}`}
      onClick={(e) => { e.stopPropagation(); onMarkPaid(row); }}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-dna-link hover:bg-dna-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus">
      <Banknote className="h-5 w-5" />
    </button>
  );
}

function SortHead({ label, k, sort, onSort, right }) {
  const active = sort.key === k;
  const Icon = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th scope="col" aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} className={cn(TH, right && 'text-right')}>
      <button type="button" onClick={() => onSort(k)}
        className={cn('inline-flex min-h-[32px] items-center gap-1 text-[13px] font-semibold hover:text-dna-primary', active && 'text-dna-primary')}>
        {label}{active && <Icon className="h-3.5 w-3.5" />}
      </button>
    </th>
  );
}

export default function ExpensesTable({ rows, today, catName, sort, onSort, ...actions }) {
  return (
    <>
      <table className="hidden w-full lg:table">
        <thead className="whitespace-nowrap border-b border-dna-line">
          <tr>
            <th scope="col" className={TH}>Status</th>
            <th scope="col" className={TH}>Fälligkeit</th>
            <th scope="col" className={TH}>Belegnummer</th>
            <th scope="col" className={TH}>Lieferant</th>
            <SortHead label="Hinzugefügt" k="created_at" sort={sort} onSort={onSort} />
            <SortHead label="Belegdatum" k="beleg_datum" sort={sort} onSort={onSort} />
            <SortHead label="Betrag (Netto)" k="netto" sort={sort} onSort={onSort} right />
            <th scope="col" className={cn(TH, 'text-right')}>Offen (Brutto)</th>
            <th scope="col" className={TH}>Kategorie</th>
            <th scope="col" className="w-24"><span className="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-dna-line text-[14px]">
          {rows.map((r) => {
            const due = dueLabel(r, today);
            const cat = categoryLabel(r, catName);
            const s = EXPENSE_STATUS[r.view];
            return (
              <tr key={r.id} className="h-14 cursor-pointer hover:bg-dna-warm/60" onClick={() => actions.onOpen(r)}>
                <td className="px-3"><StatusBadge label={s.label} className={s.class} /></td>
                <td className={cn('whitespace-nowrap px-3', TONE[due.tone])}>{due.text}</td>
                <td className="max-w-[11rem] truncate px-3"><BelegNr row={r} /></td>
                <td className="max-w-[14rem] truncate px-3 text-dna-text">{r.lieferant_name || '—'}</td>
                <td className="whitespace-nowrap px-3 text-dna-muted">{fmtDate(r.created_at)}</td>
                <td className="whitespace-nowrap px-3 text-dna-muted">{fmtDate(r.beleg_datum)}</td>
                <td className={cn('whitespace-nowrap px-3 text-right tabular-nums', r.view === 'storniert' ? 'text-dna-placeholder line-through' : 'text-dna-text')}>
                  {fmtEUR(neg(nettoOf(r)))}
                </td>
                <td className="whitespace-nowrap px-3 text-right tabular-nums text-dna-muted">{fmtEUR(offenOf(r))}</td>
                <td className="px-3">{cat && <span className="inline-block max-w-[10rem] truncate rounded-md border border-dna-line px-2 py-0.5 text-[12px] text-dna-text">{cat}</span>}</td>
                <td className="px-1"><div className="flex items-center justify-end"><PayButton row={r} onMarkPaid={actions.onMarkPaid} /><RowMenu row={r} {...actions} /></div></td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ul className="divide-y divide-dna-line lg:hidden">
        {rows.map((r) => {
          const due = dueLabel(r, today);
          const cat = categoryLabel(r, catName);
          const s = EXPENSE_STATUS[r.view];
          return (
            <li key={r.id} className="flex items-start gap-1 py-3 pl-4 pr-1 active:bg-dna-warm" onClick={() => actions.onOpen(r)}>
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge label={s.label} className={s.class} />
                  {due.text !== '—' && <span className={cn('text-[13px]', TONE[due.tone])}>{due.text}</span>}
                </div>
                <p className="truncate text-[15px] font-semibold text-dna-primary">{r.lieferant_name || 'Unbekannter Lieferant'}</p>
                <p className="flex min-w-0 items-center gap-1.5 text-[13px] text-dna-muted">
                  <BelegNr row={r} /><span aria-hidden="true">·</span><span className="shrink-0">{fmtDate(r.beleg_datum)}</span>
                </p>
                {cat && <span className="inline-block max-w-full truncate rounded-md border border-dna-line px-2 py-0.5 text-[12px] text-dna-text">{cat}</span>}
              </div>
              <div className="pt-0.5 text-right">
                <p className={cn('text-[15px] font-semibold tabular-nums', r.view === 'storniert' ? 'text-dna-placeholder line-through' : 'text-dna-primary')}>{fmtEUR(neg(nettoOf(r)))}</p>
                <p className="text-[12px] text-dna-muted">netto</p>
                {offenOf(r) > 0 && <p className="mt-1 text-[12px] tabular-nums text-dna-due-fg">offen {fmtEUR(offenOf(r))}</p>}
              </div>
              <div className="flex flex-col items-center">
                <RowMenu row={r} {...actions} />
                <PayButton row={r} onMarkPaid={actions.onMarkPaid} />
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
